/**
 * useEmployees Hook (V86)
 *
 * httpsCallable을 통해 직원 목록을 조회하는 Hook입니다.
 *
 * **기능:**
 * - 백엔드 getEmployees callable 함수 호출
 * - 검색/필터 기능
 * - 로딩/에러 상태 관리
 *
 * **V86 변경:**
 * - Firestore 직접 접근 → httpsCallable 방식으로 전환
 * - 보안 규칙 위반 문제 해결
 *
 * **V30.3:**
 * - userType='employee'인 문서만 조회 (admin 계정 제외)
 *
 * **사용 예시:**
 * ```tsx
 * const { employees, isLoading, error } = useEmployees({
 *   searchText: '김철수',
 *   orgUnitId: 'org_team_dev',
 *   status: 'active'
 * });
 * ```
 */

import { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';
import { Employee, EmployeeListFilter } from '../types/employee';

/**
 * useEmployees Hook 반환 타입
 */
export interface UseEmployeesResult {
  /** 직원 목록 */
  employees: Employee[];

  /** 로딩 중 여부 */
  isLoading: boolean;

  /** 에러 */
  error: Error | null;

  // V146_DEBUG: 호출 상태 추적용
  /** 호출 상태: 'loading' | 'ok' | 'error' | 'waiting' (스코프 대기) */
  callStatus: 'loading' | 'ok' | 'error' | 'waiting';
  /** 에러 메시지 (있으면) */
  errorMessage: string | null;
  /** 원본 응답 전체 (디버그용) */
  rawResponse: any | null;
}

/**
 * 직원 목록 조회 Hook
 *
 * Firestore의 `users` 컬렉션에서 직원 목록을 조회하고,
 * 필터 조건에 맞게 클라이언트 단에서 필터링합니다.
 *
 * **구현 방식:**
 * - getDocs를 사용하여 한 번에 전체 목록 조회 (실시간 구독 X)
 * - 이유: 직원 목록은 자주 변경되지 않으며, 페이지 진입 시점의 스냅샷으로 충분
 * - 향후 데이터가 많아지면 Firestore Query where 조건으로 서버 사이드 필터 적용 가능
 *
 * **필터링:**
 * - searchText: 이름/이메일/사번에 포함되는지 체크 (대소문자 무시)
 * - orgUnitId: 'all'이 아니면 정확히 일치하는 조직만
 * - status: 'all'이 아니면 정확히 일치하는 상태만
 *
 * @param filters 검색/필터 조건
 * @returns UseEmployeesResult
 */
export function useEmployees(filters: EmployeeListFilter = {}): UseEmployeesResult {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  // V146_DEBUG: 호출 상태 추적
  const [callStatus, setCallStatus] = useState<'loading' | 'ok' | 'error' | 'waiting'>('waiting');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [rawResponse, setRawResponse] = useState<any | null>(null);

  useEffect(() => {
    // Firebase가 초기화되지 않은 경우 (DEMO 모드)
    if (!functions) {
      setEmployees([]);
      setIsLoading(false);
      setCallStatus('error');
      setErrorMessage('Firebase Functions not initialized');
      return;
    }

    // V137.8: 필터가 비어있으면 호출하지 않음 (스코프 로딩 대기 중)
    // OrgMembersPage에서 스코프 준비 전 빈 객체 {}를 전달함
    // V28.2: 'all'은 유효한 필터값이므로 truthy로 취급
    //        - HrEmployeesPage에서 status='all', orgUnitId='all'로 초기화됨
    //        - 이 경우 전체 직원 조회가 정상 동작해야 함
    // V54.9: requestedCompanyCode도 유효한 필터로 취급
    //        - EmployeePickerPanel에서 companyCode만 전달해도 조회 가능
    const hasSearchText = !!filters.searchText;
    const hasOrgUnitId = !!filters.orgUnitId && filters.orgUnitId !== '';
    const hasOrgUnitIds = !!filters.orgUnitIds && filters.orgUnitIds.length > 0;
    const hasStatus = !!filters.status && filters.status !== '';
    const hasRequestedCompanyCode = !!filters.requestedCompanyCode;
    const hasForTransfer = !!filters.forTransfer;
    const hasForSeatMap = !!filters.forSeatMap;
    const hasAnyFilter = hasSearchText || hasOrgUnitId || hasOrgUnitIds || hasStatus || hasRequestedCompanyCode || hasForTransfer || hasForSeatMap;

    if (!hasAnyFilter) {
      // 필터 없음 - 스코프 로딩 중으로 간주하고 대기
      // (빈 문자열이나 undefined만 해당, 'all'은 유효한 필터)
      setEmployees([]);
      setIsLoading(true);
      setCallStatus('waiting');
      return;
    }

    // 직원 목록 조회 (V86: httpsCallable 사용)
    const fetchEmployees = async () => {
      try {
        setIsLoading(true);
        setError(null);
        setCallStatus('loading');
        setErrorMessage(null);

        // V137.1: orgUnitIds, scopeMode 지원
        // V29.15: requestedCompanyCode 지원
        // V114.4: forProjectStaff 지원
        const getEmployeesFn = httpsCallable<
          {
            searchText?: string;
            orgUnitId?: string;
            orgUnitIds?: string[];
            scopeMode?: 'SELF' | 'SUBTREE' | 'ALL';
            status?: string;
            requestedCompanyCode?: 'KR' | 'CN' | 'ALL';
            forProjectStaff?: boolean;
            forTransfer?: boolean;
            forSeatMap?: boolean;
          },
          { success: boolean; employees: Employee[]; debugMeta?: any }
        >(functions!, 'getEmployees');

        const result = await getEmployeesFn({
          searchText: filters.searchText,
          orgUnitId: filters.orgUnitId,
          orgUnitIds: filters.orgUnitIds,
          scopeMode: filters.scopeMode,
          status: filters.status,
          requestedCompanyCode: filters.requestedCompanyCode,
          forProjectStaff: filters.forProjectStaff,
          forTransfer: filters.forTransfer,
          forSeatMap: filters.forSeatMap,
        });

        // V146_DEBUG: 원본 응답 저장
        setRawResponse(result.data);

        if (result.data.success) {
          setEmployees(result.data.employees);
          setCallStatus('ok');
        } else {
          const msg = '직원 목록 조회 실패 (success=false)';
          setCallStatus('error');
          setErrorMessage(msg);
          throw new Error(msg);
        }

        setIsLoading(false);
      } catch (err: any) {
        const errMsg = err?.message || err?.code || String(err);
        // V148.10: 에러 로그는 유지 (운영에서도 필요)
        console.error('[useEmployees] 직원 목록 조회 실패:', errMsg);
        setError(err as Error);
        setCallStatus('error');
        setErrorMessage(errMsg);
        setIsLoading(false);
      }
    };

    fetchEmployees();
  }, [filters.searchText, filters.orgUnitId, filters.orgUnitIds, filters.scopeMode, filters.status, filters.requestedCompanyCode, filters.forProjectStaff, filters.forTransfer, filters.forSeatMap]);

  return {
    employees,
    isLoading,
    error,
    // V146_DEBUG: 호출 상태 추적
    callStatus,
    errorMessage,
    rawResponse,
  };
}

/**
 * V86: 필터링은 이제 백엔드에서 처리됩니다.
 * 이전 filterEmployees 함수는 제거됨.
 */

/**
 * ============================================
 * TODO: 성능 최적화 (향후)
 * ============================================
 *
 * 현재는 클라이언트 단에서 전체 목록을 가져와 필터링합니다.
 * 직원 수가 수백~수천 명 이상으로 늘어나면 아래 최적화가 필요합니다:
 *
 * 1. **서버 사이드 필터링:**
 *    - Firestore Query where 조건 사용
 *    - 예: where('status', '==', 'active')
 *    - 예: where('orgUnitId', '==', 'org_team_dev')
 *
 * 2. **페이지네이션:**
 *    - startAfter, limit를 사용한 커서 기반 페이지네이션
 *    - UI에 "더 보기" 버튼 또는 무한 스크롤 추가
 *
 * 3. **검색 최적화:**
 *    - searchText는 Firestore Native로 처리하기 어려움
 *    - Algolia, Elasticsearch 등 외부 검색 엔진 연동 고려
 *    - 또는 Firebase Extensions의 Firestore Full-Text Search 사용
 *
 * 4. **캐싱:**
 *    - React Query 또는 SWR 도입
 *    - 동일한 쿼리에 대해 캐시 활용
 *
 * 5. **인덱스:**
 *    - Firestore 복합 인덱스 생성
 *    - Firebase Console에서 설정
 */

/**
 * ============================================
 * TODO: 실시간 구독으로 전환 (선택)
 * ============================================
 *
 * 현재는 getDocs를 사용하여 한 번만 조회합니다.
 * 직원 데이터가 실시간으로 변경되는 것을 반영하려면:
 *
 * ```tsx
 * useEffect(() => {
 *   if (!db) return;
 *
 *   const usersCollection = collection(db, 'users');
 *   const q = query(usersCollection);
 *
 *   // onSnapshot으로 실시간 구독
 *   const unsubscribe = onSnapshot(
 *     q,
 *     (snapshot) => {
 *       const allEmployees = snapshot.docs.map(mapUserDocToEmployee);
 *       const filtered = filterEmployees(allEmployees, filters);
 *       setEmployees(filtered);
 *       setIsLoading(false);
 *     },
 *     (err) => {
 *       console.error('실시간 구독 오류:', err);
 *       setError(err as Error);
 *       setIsLoading(false);
 *     }
 *   );
 *
 *   return () => unsubscribe();
 * }, [filters]);
 * ```
 *
 * **장점:**
 * - 다른 사용자가 직원 정보를 수정하면 자동으로 UI 업데이트
 *
 * **단점:**
 * - Firestore 읽기 비용 증가 (변경될 때마다 과금)
 * - 네트워크 트래픽 증가
 *
 * **권장:**
 * - 직원 목록은 자주 변경되지 않으므로, 현재 getDocs 방식 유지
 * - 필요시 "새로고침" 버튼으로 수동 갱신 지원
 */

/**
 * ============================================
 * useEmployee Hook (단건 조회)
 * ============================================
 */

/**
 * useEmployee Hook 반환 타입
 */
export interface UseEmployeeResult {
  /** 직원 정보 */
  employee: Employee | null;

  /** 로딩 중 여부 */
  isLoading: boolean;

  /** 에러 */
  error: Error | null;

  /** 데이터 다시 가져오기 */
  refetch: () => void;
}

/**
 * 단일 직원 조회 Hook
 *
 * Firestore의 `users` 컬렉션에서 특정 ID의 직원 정보를 조회합니다.
 *
 * **사용 예시:**
 * ```tsx
 * const { employee, isLoading, error } = useEmployee('user_001');
 * ```
 *
 * @param employeeId 직원 ID (Firestore 문서 ID)
 * @returns UseEmployeeResult
 */
export function useEmployee(employeeId: string | undefined): UseEmployeeResult {
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [refreshKey, setRefreshKey] = useState<number>(0);

  // V40.2: refetch 함수 추가
  const refetch = () => {
    setRefreshKey(prev => prev + 1);
  };

  useEffect(() => {
    // employeeId가 없는 경우
    if (!employeeId) {
      setEmployee(null);
      setIsLoading(false);
      return;
    }

    // Firebase가 초기화되지 않은 경우 (DEMO 모드)
    if (!functions) {
      setEmployee(null);
      setIsLoading(false);
      return;
    }

    // 직원 정보 조회 (V86: httpsCallable 사용)
    const fetchEmployee = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const getEmployeeFn = httpsCallable<
          { employeeId: string },
          { success: boolean; employee: Employee }
        >(functions!, 'getEmployee');

        const result = await getEmployeeFn({ employeeId });

        if (result.data.success && result.data.employee) {
          setEmployee(result.data.employee);
        } else {
          setEmployee(null);
          setError(new Error('해당 직원을 찾을 수 없습니다.'));
        }

        setIsLoading(false);
      } catch (err: any) {
        console.error('직원 정보 조회 실패:', err);
        setError(err as Error);
        setEmployee(null);
        setIsLoading(false);
      }
    };

    fetchEmployee();
  }, [employeeId, refreshKey]);

  return { employee, isLoading, error, refetch };
}

/**
 * ============================================
 * TODO: 실시간 구독으로 전환 (useEmployee)
 * ============================================
 *
 * 현재는 getDoc을 사용하여 한 번만 조회합니다.
 * 직원 정보가 실시간으로 변경되는 것을 반영하려면:
 *
 * ```tsx
 * useEffect(() => {
 *   if (!employeeId || !db) return;
 *
 *   const docRef = doc(db, 'users', employeeId);
 *
 *   // onSnapshot으로 실시간 구독
 *   const unsubscribe = onSnapshot(
 *     docRef,
 *     (docSnap) => {
 *       if (docSnap.exists()) {
 *         const employeeData = mapUserDocToEmployee(docSnap as any);
 *         setEmployee(employeeData);
 *       } else {
 *         setEmployee(null);
 *         setError(new Error('해당 직원을 찾을 수 없습니다.'));
 *       }
 *       setIsLoading(false);
 *     },
 *     (err) => {
 *       console.error('실시간 구독 오류:', err);
 *       setError(err as Error);
 *       setIsLoading(false);
 *     }
 *   );
 *
 *   return () => unsubscribe();
 * }, [employeeId]);
 * ```
 *
 * **장점:**
 * - 다른 사용자가 직원 정보를 수정하면 자동으로 UI 업데이트
 * - 특히 상세 페이지에서는 실시간 동기화가 유용할 수 있음
 *
 * **단점:**
 * - Firestore 읽기 비용 증가
 *
 * **권장:**
 * - 상세 페이지는 조회 빈도가 낮으므로, 실시간 구독도 고려 가능
 * - 비용이 우려된다면 현재 getDoc 방식 유지
 */
